import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import type { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { Color } from 'shared/utils/colors';
import { PieChart } from '@mui/x-charts/PieChart';
import { formatDonutSliceTooltipValue } from '../../formatters/buildDonutLegendItems';
import { getSliceGradientFill } from '../../theme/chartGradients';
import {
  CHART_DONUT_STYLE,
  getChartTooltipSx,
} from '../../theme/muiChartSlotProps';
import {
  CHART_DONUT_DASHBOARD_PIE_MAX_SIZE,
  CHART_DONUT_INNER_RADIUS,
  CHART_TRIPLET_CARD_GAUGE_SIZE,
  CHART_TRIPLET_GAUGE_SIZE,
  getChartDonutMargin,
} from '../../tokens/muiChartLayout';
import { ChartSvgDefs } from '../ChartSvgDefs';
import { DonutChartCenterTotal } from '../DonutChartCenterTotal';
import { TripletGaugePart } from '../TripletGaugePart';

const centeredCellSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
} satisfies SxProps<Theme>;

export function TripletCountDisplay({
  chartId,
  value,
  label,
  size = CHART_DONUT_DASHBOARD_PIE_MAX_SIZE,
  variant = 'dashboard',
}: Readonly<{
  chartId: string;
  value: number | undefined;
  label: string;
  size?: number;
  variant?: 'dashboard' | 'details';
}>): ReactElement | null {
  const intl = useIntl();

  if (value === undefined) {
    return null;
  }

  const isDashboard = variant === 'dashboard';
  const fill = getSliceGradientFill(chartId, 0);
  const hasCount = value > 0;

  return (
    <Box sx={centeredCellSx}>
      <Box
        sx={{
          position: 'relative',
          flex: `0 0 ${size}px`,
          width: size,
          height: size,
          overflow: 'visible',
        }}
      >
        <ChartSvgDefs chartId={chartId} colors={[Color.statusSuccessGreen]} />
        <PieChart
          series={[
            {
              data: [
                {
                  id: 0,
                  value: hasCount ? value : 1,
                  label,
                  color: hasCount ? fill : Color.solidGray,
                },
              ],
              innerRadius: CHART_DONUT_INNER_RADIUS,
              paddingAngle: isDashboard
                ? CHART_DONUT_STYLE.dashboardPaddingAngle
                : CHART_DONUT_STYLE.paddingAngle,
              cornerRadius: isDashboard
                ? CHART_DONUT_STYLE.dashboardCornerRadius
                : CHART_DONUT_STYLE.cornerRadius,
              arcLabelMinAngle: CHART_DONUT_STYLE.arcLabelMinAngle,
              valueFormatter: () =>
                formatDonutSliceTooltipValue(
                  intl,
                  value,
                  Math.max(value, 1)
                ),
              highlightScope: { highlight: 'item', fade: 'global' },
              highlighted: {
                additionalRadius: isDashboard
                  ? CHART_DONUT_STYLE.dashboardHighlightAdditionalRadius
                  : CHART_DONUT_STYLE.highlightAdditionalRadius,
              },
            },
          ]}
          width={size}
          height={size}
          margin={getChartDonutMargin(variant)}
          hideLegend
          slotProps={{ tooltip: { sx: getChartTooltipSx() } }}
          sx={{ overflow: 'visible' }}
        />
        <DonutChartCenterTotal total={value} />
      </Box>
    </Box>
  );
}

export function TripletGaugeDisplay({
  chartId,
  gaugeIndex,
  value,
  color,
  label,
  size = CHART_TRIPLET_GAUGE_SIZE,
  variant = 'details',
}: Readonly<{
  chartId: string;
  gaugeIndex: number;
  value: number | undefined;
  color: string;
  label: string;
  size?: number;
  variant?: 'dashboard' | 'details';
}>): ReactElement | null {
  if (value === undefined) {
    return null;
  }

  return (
    <Box sx={centeredCellSx}>
      <TripletGaugePart
        chartId={chartId}
        gaugeIndex={gaugeIndex}
        value={value}
        color={color}
        label={label}
        size={size}
        variant={variant}
      />
    </Box>
  );
}

export const TRIPLET_GAUGE_COLORS = {
  failedFactor: Color.primaryOrange,
  result: Color.blue,
} as const;

export { CHART_TRIPLET_CARD_GAUGE_SIZE };
